import React, { Component } from 'react';
import { Container, Row, Col, Input, Button, Label } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircle, faEdit, faGripLines, faPlus, faTimes, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { faCircle as faCircleOutline } from '@fortawesome/free-regular-svg-icons';
import ReactDragListView from 'react-drag-listview';
import CollapseCard from '../CollapseCard/CollapseCard';
import CompanyContext from '../../auth/CompanyContext';
import ConfirmButton from '../Modal/ConfirmButton';

const emptyContactField = {
    type: '',
    validation: '',
    validationHint: '',
    screenTab: 'demographics',
    sortOrder: '',
    unique: false,
    multiline: false,
    deleted: false,
    listItems: []
};

class ContactFieldsEditor extends Component {
    static contextType = CompanyContext;

    constructor(props) {
        super(props);
        this.state = {
            notesCategories: [],
            contactFields: [],
            editField: null,
            editListValueText: '',
            editListItemValues: {},
            expandedAddSection: null,
            newListValueText: {
                details: '',
                demographics: ''
            },
            newContactFields: {
                details: { ...emptyContactField, screenTab: 'details' },
                demographics: { ...emptyContactField, screenTab: 'demographics' }
            }
        };
    }

    componentDidMount() {
        this.refreshFields();
    }

    refreshFields = () => {
        this.getNotesCategories();
        this.getContactFields();
    }

    getNotesCategories() {
        fetch('/api/notesCategories/', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(results => {
                this.setState({ notesCategories: results.data || [] });
            });
    }

    getContactFields() {
        fetch('/api/notesCategories/contactFields', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(results => {
                this.setState({ contactFields: results.data || [] });
            });
    }

    getContactListFields = (notesCategories = this.state.notesCategories) => {
        const contactParent = notesCategories.find(parentCategory => parentCategory.type === 'Contact');
        return contactParent?.children || [];
    }

    getContactFieldWithList = (field) => {
        if (field.list != null) return field;

        const categoryField = this.getContactListFields()
            .find(childCategory => childCategory.ID === field.ID);
        return {
            ...field,
            list: categoryField?.list || []
        };
    }

    sortListItems = (listItems = []) => (
        [...listItems].sort((a, b) => {
            let sortA = parseInt(a.sortOrder);
            let sortB = parseInt(b.sortOrder);
            sortA = Number.isNaN(sortA) ? Number.MAX_SAFE_INTEGER : sortA;
            sortB = Number.isNaN(sortB) ? Number.MAX_SAFE_INTEGER : sortB;
            if (sortA !== sortB) return sortA - sortB;
            return String(a.description || '').localeCompare(String(b.description || ''));
        })
    )

    getValidationLabel = (validation) => {
        switch (validation) {
            case 'date':
                return 'Date';
            case 'checkbox':
                return 'Checkbox';
            case 'list':
                return 'List';
            default:
                return 'Text';
        }
    }

    getOptionsSummary = (field) => {
        const options = [];
        if (field.multiline === true || field.multiline === 1) options.push('Multiline');
        if (field.unique === true || field.unique === 1) options.push('Unique');
        if (field.validation === 'list') options.push((field.list || []).length + ' list options');
        return options.join(', ') || '-';
    }

    renderStatusDot = (field) => {
        const isActive = !(field.deleted === true || field.deleted === 1);
        return (
            <FontAwesomeIcon
                icon={isActive ? faCircle : faCircleOutline}
                className={isActive ? 'iconActive' : 'iconInactive'}
                title={isActive ? 'Active' : 'Inactive'}
            />
        );
    }

    getFieldsByTab = (screenTab) => {
        return this.state.contactFields
            .filter(field => String(field.screenTab || '').toLowerCase() === screenTab)
            .sort((a, b) => {
                let sortA = parseInt(a.sortOrder);
                let sortB = parseInt(b.sortOrder);
                sortA = Number.isNaN(sortA) ? Number.MAX_SAFE_INTEGER : sortA;
                sortB = Number.isNaN(sortB) ? Number.MAX_SAFE_INTEGER : sortB;
                if (sortA !== sortB) return sortA - sortB;
                return String(a.type || '').localeCompare(String(b.type || ''));
            })
            .map(this.getContactFieldWithList);
    }

    updateEditFieldState = (key, value) => {
        this.setState(prevState => ({
            editField: {
                ...prevState.editField,
                [key]: value
            }
        }));
    }

    updateNewContactFieldState = (screenTab, key, value) => {
        this.setState(prevState => ({
            newContactFields: {
                ...prevState.newContactFields,
                [screenTab]: {
                    ...prevState.newContactFields[screenTab],
                    [key]: value
                }
            }
        }));
    }

    persistContactField = (field) => {
        return fetch('/api/notesCategories/contactFields/' + field.ID, {
            method: 'PUT',
            body: JSON.stringify(field),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json());
    }

    saveEditField = () => {
        if (!this.state.editField || String(this.state.editField.type || '').trim() === '') return;

        const field = {
            ...this.state.editField,
            type: String(this.state.editField.type || '').trim()
        };

        this.setState({ editField: null, editListValueText: '' });
        this.persistContactField(field).then(this.refreshFields);
    }

    deleteContactField = (field) => {
        this.persistContactField({ ...field, deleted: true }).then(() => {
            this.setState(prevState => ({
                editField: prevState.editField?.ID === field.ID ? null : prevState.editField
            }));
            this.refreshFields();
        });
    }

    createContactField = (screenTab) => {
        const field = this.state.newContactFields[screenTab] || { ...emptyContactField, screenTab };
        const type = String(field.type || '').trim();
        if (type === '') return;

        const listItems = (field.listItems || [])
            .map(item => String(item || '').trim())
            .filter(item => item !== '');
        const fieldsInSection = this.getFieldsByTab(screenTab);
        const fieldToCreate = {
            ...field,
            type,
            screenTab,
            sortOrder: (fieldsInSection.length + 1) * 10
        };
        delete fieldToCreate.listItems;

        fetch('/api/notesCategories/contactFields', {
            method: 'POST',
            body: JSON.stringify(fieldToCreate),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (field.validation !== 'list' || !results.data) return results;
                return listItems.reduce(
                    (promise, description) => promise.then(() => this.createListValue(results.data, description)),
                    Promise.resolve()
                );
            })
            .then(() => {
                this.setState(prevState => ({
                    expandedAddSection: null,
                    newListValueText: {
                        ...prevState.newListValueText,
                        [screenTab]: ''
                    },
                    newContactFields: {
                        ...prevState.newContactFields,
                        [screenTab]: { ...emptyContactField, screenTab }
                    }
                }));
                this.refreshFields();
            });
    }

    createListValue = (commentTypeId, description) => {
        const cleanDescription = String(description || '').trim();
        if (cleanDescription === '') return Promise.resolve();

        return fetch('/api/notesCategories/', {
            method: 'POST',
            body: JSON.stringify({ commentTypeId, description: cleanDescription }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json());
    }

    addExistingListValue = (field) => {
        this.createListValue(field.ID, this.state.editListValueText)
            .then(() => {
                this.setState({ editListValueText: '' });
                this.refreshFields();
            });
    }

    updateListItemDraft = (itemID, value) => {
        this.setState(prevState => ({
            editListItemValues: {
                ...prevState.editListItemValues,
                [itemID]: value
            }
        }));
    }

    getListItemDraftValue = (item) => (
        this.state.editListItemValues[item.ID] != null
            ? this.state.editListItemValues[item.ID]
            : (item.description || '')
    )

    saveListItemValue = (item) => {
        const description = String(this.getListItemDraftValue(item) || '').trim();
        if (description === '' || description === String(item.description || '').trim()) return;

        fetch('/api/notesCategoryLists/' + item.ID, {
            method: 'PUT',
            body: JSON.stringify({ description }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(() => {
                this.setState(prevState => {
                    const editListItemValues = { ...prevState.editListItemValues };
                    delete editListItemValues[item.ID];
                    return { editListItemValues };
                });
                this.refreshFields();
            });
    }

    persistListItemOrder = (listItems) => {
        return fetch('/api/notesCategoryLists/reorder', {
            method: 'PUT',
            body: JSON.stringify({
                listItems: listItems.map(item => ({
                    id: item.ID,
                    sortOrder: item.sortOrder
                }))
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json());
    }

    reorderListItems = (field, fromIndex, toIndex) => {
        if (fromIndex === toIndex) return;

        const fieldWithList = this.getContactFieldWithList(field);
        const orderedItems = this.sortListItems(fieldWithList.list || []);
        const nextItems = [...orderedItems];
        const item = nextItems.splice(fromIndex, 1)[0];
        if (item == null) return;
        nextItems.splice(toIndex, 0, item);

        const reorderedItems = nextItems.map((listItem, index) => ({
            ...listItem,
            sortOrder: (index + 1) * 10
        }));

        this.setState(prevState => ({
            contactFields: prevState.contactFields.map(contactField => (
                contactField.ID === field.ID
                    ? { ...contactField, list: reorderedItems }
                    : contactField
            ))
        }));

        this.persistListItemOrder(reorderedItems)
            .then(this.refreshFields)
            .catch(this.refreshFields);
    }

    addNewFieldListValue = (screenTab) => {
        const description = String(this.state.newListValueText[screenTab] || '').trim();
        if (description === '') return;

        this.setState(prevState => ({
            newListValueText: {
                ...prevState.newListValueText,
                [screenTab]: ''
            },
            newContactFields: {
                ...prevState.newContactFields,
                [screenTab]: {
                    ...prevState.newContactFields[screenTab],
                    listItems: [
                        ...(prevState.newContactFields[screenTab].listItems || []),
                        description
                    ]
                }
            }
        }));
    }

    removeNewFieldListValue = (screenTab, index) => {
        this.setState(prevState => ({
            newContactFields: {
                ...prevState.newContactFields,
                [screenTab]: {
                    ...prevState.newContactFields[screenTab],
                    listItems: (prevState.newContactFields[screenTab].listItems || [])
                        .filter((item, itemIndex) => itemIndex !== index)
                }
            }
        }));
    }

    updateNewFieldListValue = (screenTab, index, value) => {
        this.setState(prevState => ({
            newContactFields: {
                ...prevState.newContactFields,
                [screenTab]: {
                    ...prevState.newContactFields[screenTab],
                    listItems: (prevState.newContactFields[screenTab].listItems || [])
                        .map((item, itemIndex) => itemIndex === index ? value : item)
                }
            }
        }));
    }

    reorderNewFieldListItems = (screenTab, fromIndex, toIndex) => {
        if (fromIndex === toIndex) return;

        this.setState(prevState => {
            const listItems = [...(prevState.newContactFields[screenTab].listItems || [])];
            const item = listItems.splice(fromIndex, 1)[0];
            if (item == null) return {};
            listItems.splice(toIndex, 0, item);

            return {
                newContactFields: {
                    ...prevState.newContactFields,
                    [screenTab]: {
                        ...prevState.newContactFields[screenTab],
                        listItems
                    }
                }
            };
        });
    }

    handleDeleteListValue = (item) => {
        fetch('/api/notesCategoryLists/' + item.ID, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(this.refreshFields);
    }

    persistContactFieldOrder = (contactFields) => {
        return fetch('/api/notesCategories/contactFields/reorder', {
            method: 'PUT',
            body: JSON.stringify({
                contactFields: contactFields.map(field => ({
                    id: field.ID,
                    sortOrder: field.sortOrder,
                    screenTab: field.screenTab
                }))
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json());
    }

    reorderFields = (screenTab, fromIndex, toIndex) => {
        if (fromIndex === toIndex) return;

        const orderedFields = this.getFieldsByTab(screenTab);
        const nextFields = [...orderedFields];
        const item = nextFields.splice(fromIndex, 1)[0];
        if (item == null) return;
        nextFields.splice(toIndex, 0, item);

        const reorderedFields = nextFields.map((field, index) => ({
            ...field,
            screenTab,
            sortOrder: (index + 1) * 10
        }));

        this.setState(prevState => {
            const reorderedMap = new Map(reorderedFields.map(field => [field.ID, field]));
            return {
                contactFields: prevState.contactFields.map(field => (
                    reorderedMap.get(field.ID) || field
                ))
            };
        });

        this.persistContactFieldOrder(reorderedFields)
            .then(this.refreshFields)
            .catch(this.refreshFields);
    }

    renderValidationSelect = (value, onChange) => (
        <Input type="select" value={value || ''} onChange={onChange}>
            <option value="">Text</option>
            <option value="date">Date</option>
            <option value="checkbox">Checkbox</option>
            <option value="list">List</option>
        </Input>
    )

    renderEditListOptions = (field) => {
        const listItems = this.sortListItems(field.list || []);
        const dragProps = {
            onDragEnd: (fromIndex, toIndex) => this.reorderListItems(field, fromIndex, toIndex),
            nodeSelector: `div.contactListOptionRow-${field.ID}`,
            handleSelector: 'div.contactListOptionDragHandle'
        };

        return (
            <Row className="mt-3">
                <Col md={{ size: 10, offset: 1 }}>
                    <Label>List Options</Label>
                    <ReactDragListView {...dragProps}>
                        {listItems.map(item => {
                            const draftValue = this.getListItemDraftValue(item);
                            const isChanged = String(draftValue || '').trim() !== String(item.description || '').trim();

                            return (
                                <div key={item.ID} className={`contactListOptionRow contactListOptionRow-${field.ID}`}>
                                    <Row className="mb-2 align-items-center">
                                        <Col md={1} className="text-left">
                                            <div className="contactListOptionDragHandle dragIcon px-2" title="Drag to reorder">
                                                <FontAwesomeIcon icon={faGripLines} />
                                            </div>
                                        </Col>
                                        <Col md={9}>
                                            <Input
                                                value={draftValue}
                                                onChange={(e) => this.updateListItemDraft(item.ID, e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && isChanged) {
                                                        e.preventDefault();
                                                        this.saveListItemValue(item);
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col md={2} className="text-right text-nowrap">
                                            {isChanged ? (
                                                <Button
                                                    className="mx-1"
                                                    color="success"
                                                    title="Save List Option"
                                                    disabled={String(draftValue || '').trim() === ''}
                                                    onClick={() => this.saveListItemValue(item)}
                                                >
                                                    <FontAwesomeIcon icon={faCheck} />
                                                </Button>
                                            ) : null}
                                            <ConfirmButton
                                                className="mx-1"
                                                color="danger"
                                                title="Delete List Option"
                                                onClick={() => this.handleDeleteListValue(item)}
                                            >
                                                <FontAwesomeIcon icon={faTrashAlt} />
                                            </ConfirmButton>
                                        </Col>
                                    </Row>
                                </div>
                            );
                        })}
                    </ReactDragListView>
                <Row className="mb-2 align-items-center">
                    <Col md={{ size: 9, offset: 1 }}>
                        <Input
                            placeholder="New list option"
                            value={this.state.editListValueText}
                            onChange={(e) => this.setState({ editListValueText: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && this.state.editListValueText.trim()) {
                                    e.preventDefault();
                                    this.addExistingListValue(field);
                                }
                            }}
                        />
                    </Col>
                    <Col md={2} className="text-right">
                        <Button
                            color="success"
                            title="Add List Option"
                            disabled={!this.state.editListValueText.trim()}
                            onClick={() => this.addExistingListValue(field)}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </Button>
                    </Col>
                </Row>
            </Col>
        </Row>
        );
    }

    renderEditFieldForm = (field) => {
        const editField = this.state.editField || field;
        const fieldWithList = this.getContactFieldWithList(field);

        return (
            <div className="border rounded p-3 mb-3">
                <Row className="mb-3">
                    <Col md={4}>
                        <Label>Field Name</Label>
                        <Input
                            value={editField.type || ''}
                            onChange={(e) => this.updateEditFieldState('type', e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <Label>Validation</Label>
                        {this.renderValidationSelect(
                            editField.validation,
                            (e) => this.updateEditFieldState('validation', e.target.value)
                        )}
                    </Col>
                    <Col md={5}>
                        <Label>Validation Hint</Label>
                        <Input
                            value={editField.validationHint || ''}
                            onChange={(e) => this.updateEditFieldState('validationHint', e.target.value)}
                        />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col md={2}>
                        <Label className="mb-0">
                            <Input
                                type="checkbox"
                                checked={editField.multiline === true || editField.multiline === 1}
                                onChange={(e) => this.updateEditFieldState('multiline', e.target.checked)}
                            /> Multiline
                        </Label>
                    </Col>
                    <Col md={2}>
                        <Label className="mb-0">
                            <Input
                                type="checkbox"
                                checked={editField.unique === true || editField.unique === 1}
                                onChange={(e) => this.updateEditFieldState('unique', e.target.checked)}
                            /> Unique
                        </Label>
                    </Col>
                    <Col md={2}>
                        <Label className="mb-0">
                            <Input
                                type="checkbox"
                                checked={!(editField.deleted === true || editField.deleted === 1)}
                                onChange={(e) => this.updateEditFieldState('deleted', !e.target.checked)}
                            /> Active
                        </Label>
                    </Col>
                    <Col md={6} className="text-right">
                        <Button
                            className="mx-1"
                            color="success"
                            title="Save Contact Field"
                            disabled={!String(editField.type || '').trim()}
                            onClick={this.saveEditField}
                        >
                            <FontAwesomeIcon icon={faCheck} />
                        </Button>
                        <Button
                            className="mx-1"
                            color="secondary"
                            title="Cancel"
                            onClick={() => this.setState({ editField: null, editListValueText: '' })}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </Button>
                    </Col>
                </Row>
                {editField.validation === 'list' ? this.renderEditListOptions(fieldWithList) : null}
            </div>
        );
    }

    renderFieldRow = (field) => {
        const isEditing = this.state.editField?.ID === field.ID;

        return (
            <div key={field.ID} className={`contactFieldRow contactFieldRow-${field.screenTab}`}>
                <Row className="py-2 align-items-center border-bottom">
                    <Col md={1} className="text-left">
                        {this.renderStatusDot(field)}
                    </Col>
                    <Col md={1} className="text-left">
                        <div className="contactFieldDragHandle dragIcon px-2" title="Drag to reorder">
                            <FontAwesomeIcon icon={faGripLines} />
                        </div>
                    </Col>
                    <Col md={3} className="text-left">{field.type}</Col>
                    <Col md={2} className="text-left">{this.getValidationLabel(field.validation)}</Col>
                    <Col md={2} className="text-left">{field.validationHint || '-'}</Col>
                    <Col md={1} className="text-left">{this.getOptionsSummary(field)}</Col>
                    <Col md={2} className="text-right text-nowrap">
                        <Button
                            className="mx-1"
                            color="primary"
                            title="Edit Contact Field"
                            onClick={() => this.setState({ editField: { ...field }, editListValueText: '' })}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <ConfirmButton
                            className="mx-1"
                            color="danger"
                            title="Delete Contact Field"
                            body="Delete this contact field?"
                            onClick={() => this.deleteContactField(field)}
                        >
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </ConfirmButton>
                    </Col>
                </Row>
                {isEditing ? this.renderEditFieldForm(field) : null}
            </div>
        );
    }

    renderNewListOptions = (screenTab, field) => {
        const dragProps = {
            onDragEnd: (fromIndex, toIndex) => this.reorderNewFieldListItems(screenTab, fromIndex, toIndex),
            nodeSelector: `div.newContactListOptionRow-${screenTab}`,
            handleSelector: 'div.newContactListOptionDragHandle'
        };

        return (
            <Row className="mt-3">
                <Col md={{ size: 10, offset: 1 }}>
                    <Label>List Options</Label>
                    <ReactDragListView {...dragProps}>
                        {(field.listItems || []).map((item, index) => (
                            <div key={index} className={`newContactListOptionRow newContactListOptionRow-${screenTab}`}>
                                <Row className="mb-2 align-items-center">
                                    <Col md={1} className="text-left">
                                        <div className="newContactListOptionDragHandle dragIcon px-2" title="Drag to reorder">
                                            <FontAwesomeIcon icon={faGripLines} />
                                        </div>
                                    </Col>
                                    <Col md={9}>
                                        <Input
                                            value={item}
                                            onChange={(e) => this.updateNewFieldListValue(screenTab, index, e.target.value)}
                                        />
                                    </Col>
                                    <Col md={2} className="text-right">
                                        <Button
                                            className="mx-1"
                                            color="danger"
                                            title="Remove List Option"
                                            onClick={() => this.removeNewFieldListValue(screenTab, index)}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} />
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </ReactDragListView>
                <Row className="mb-2 align-items-center">
                    <Col md={{ size: 9, offset: 1 }}>
                        <Input
                            placeholder="New list option"
                            value={this.state.newListValueText[screenTab]}
                            onChange={(e) => this.setState(prevState => ({
                                newListValueText: {
                                    ...prevState.newListValueText,
                                    [screenTab]: e.target.value
                                }
                            }))}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && this.state.newListValueText[screenTab].trim()) {
                                    e.preventDefault();
                                    this.addNewFieldListValue(screenTab);
                                }
                            }}
                        />
                    </Col>
                    <Col md={2} className="text-right">
                        <Button
                            color="success"
                            title="Add List Option"
                            disabled={!this.state.newListValueText[screenTab].trim()}
                            onClick={() => this.addNewFieldListValue(screenTab)}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </Button>
                    </Col>
                </Row>
            </Col>
        </Row>
        );
    }

    renderNewContactFieldForm = (screenTab) => {
        const field = this.state.newContactFields[screenTab] || { ...emptyContactField, screenTab };

        return (
            <div className="border rounded p-3 mt-3 mb-4">
                <Row className="mb-3">
                    <Col md={4}>
                        <Label>Field Name</Label>
                        <Input
                            placeholder="New field"
                            value={field.type}
                            onChange={(e) => this.updateNewContactFieldState(screenTab, 'type', e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <Label>Validation</Label>
                        {this.renderValidationSelect(
                            field.validation,
                            (e) => this.updateNewContactFieldState(screenTab, 'validation', e.target.value)
                        )}
                    </Col>
                    <Col md={5}>
                        <Label>Validation Hint</Label>
                        <Input
                            placeholder="Validation hint"
                            value={field.validationHint}
                            onChange={(e) => this.updateNewContactFieldState(screenTab, 'validationHint', e.target.value)}
                        />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col md={2}>
                        <Label className="mb-0">
                            <Input
                                type="checkbox"
                                checked={field.multiline}
                                onChange={(e) => this.updateNewContactFieldState(screenTab, 'multiline', e.target.checked)}
                            /> Multiline
                        </Label>
                    </Col>
                    <Col md={2}>
                        <Label className="mb-0">
                            <Input
                                type="checkbox"
                                checked={field.unique}
                                onChange={(e) => this.updateNewContactFieldState(screenTab, 'unique', e.target.checked)}
                            /> Unique
                        </Label>
                    </Col>
                    <Col md={8} className="text-right">
                        <Button
                            className="mx-1"
                            color="success"
                            title="Add Contact Field"
                            disabled={!String(field.type || '').trim()}
                            onClick={() => this.createContactField(screenTab)}
                        >
                            <FontAwesomeIcon icon={faCheck} />
                        </Button>
                        <Button
                            className="mx-1"
                            color="secondary"
                            title="Cancel"
                            onClick={() => this.setState({ expandedAddSection: null })}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </Button>
                    </Col>
                </Row>
                {field.validation === 'list' ? this.renderNewListOptions(screenTab, field) : null}
            </div>
        );
    }

    renderAddFieldControl = (screenTab) => {
        if (this.state.expandedAddSection === screenTab) {
            return this.renderNewContactFieldForm(screenTab);
        }

        return (
            <Row className="mt-3 mb-4">
                <Col>
                    <Button
                        color="success"
                        onClick={() => this.setState({ expandedAddSection: screenTab })}
                    >
                        <FontAwesomeIcon icon={faPlus} /> Add New Field
                    </Button>
                </Col>
            </Row>
        );
    }

    renderFieldSection = (screenTab, title) => {
        const fields = this.getFieldsByTab(screenTab);
        const dragProps = {
            onDragEnd: (fromIndex, toIndex) => this.reorderFields(screenTab, fromIndex, toIndex),
            nodeSelector: `div.contactFieldRow-${screenTab}`,
            handleSelector: 'div.contactFieldDragHandle'
        };

        return (
            <>
                <Row className="mt-4 mb-2">
                    <Col>
                        <h4>{title}</h4>
                    </Col>
                </Row>
                <Row className="font-weight-bold mb-2">
                    <Col md={1} className="text-left"></Col>
                    <Col md={1} className="text-left"></Col>
                    <Col md={3} className="text-left">Field</Col>
                    <Col md={2} className="text-left">Validation</Col>
                    <Col md={2} className="text-left">Validation Hint</Col>
                    <Col md={1} className="text-left">Options</Col>
                    <Col md={2} className="text-right"></Col>
                </Row>
                <ReactDragListView {...dragProps}>
                    {fields.map(this.renderFieldRow)}
                </ReactDragListView>
                {this.renderAddFieldControl(screenTab)}
            </>
        );
    }

    render() {
        return (
            <Container fluid>
                <CollapseCard title="Contact Fields" open={false} header="h3">
                    {this.renderFieldSection('details', 'Details')}
                    {this.renderFieldSection('demographics', 'Demographics')}
                </CollapseCard>
            </Container>
        );
    };
}

export default ContactFieldsEditor;
